/**
 * The field library.
 *
 * This is the screen that makes the claim true: everything the platform can
 * ever ask about is a row here, and a new one is typed in rather than
 * deployed. The tiles at the top say what the library is made of before you
 * read a single row of it.
 */
import { useState } from "react";
import type { DataType } from "@dp/shared";
import { api } from "../../lib/api.ts";
import { Link } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import { DATA_TYPE_LABELS, type EntityRow, type FieldRow } from "../../lib/types.ts";
import { Empty, Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";

export function Fields() {
  const { can } = useAuth();
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("");
  const [showRetired, setShowRetired] = useState(false);

  const entities = useAsync(() => api.get<{ entities: EntityRow[] }>("/api/entities"), []);
  const fields = useAsync(() => {
    const query = new URLSearchParams();
    if (search.trim()) query.set("search", search.trim());
    if (entity) query.set("entity", entity);
    if (showRetired) query.set("retired", "true");
    return api.get<{ fields: FieldRow[] }>(`/api/fields?${query}`);
  }, [search, entity, showRetired]);

  const rows = fields.data?.fields ?? [];
  const inUse = rows.filter((row) => row.useCount > 0).length;
  const retired = rows.filter((row) => !row.active).length;
  const choices = rows.filter((row) => row.dataType === "choice" || row.dataType === "multi_choice").length;

  return (
    <div className="stack">
      <PageHead
        title="Field library"
        sub="Every question the platform can ask. Define one here, then attach it to as many products as you like."
        actions={can("configuration", "edit") && <Link to="/fields/new" className="btn btn-primary">New field</Link>}
      />

      <Tiles
        tiles={[
          { label: "Fields defined", value: rows.length, tone: "accent" },
          { label: "Used by a product", value: inUse, tone: "ok" },
          { label: "Choice fields", value: choices, tone: "info" },
          { label: showRetired ? "Retired" : "Entities", value: showRetired ? retired : (entities.data?.entities.length ?? 0), tone: "muted" },
        ]}
      />

      <div className="card">
        <div className="card-head filters">
          <input
            className="control" type="search" placeholder="Search by wording, key or entity…"
            value={search} onChange={(event) => setSearch(event.target.value)}
          />
          <select className="control" value={entity} onChange={(event) => setEntity(event.target.value)}>
            <option value="">Every entity</option>
            {(entities.data?.entities ?? []).map((row) => (
              <option key={row.key} value={row.key}>{row.label}</option>
            ))}
          </select>
          <label className="checkline small">
            <input type="checkbox" checked={showRetired} onChange={(event) => setShowRetired(event.target.checked)} />
            Include retired
          </label>
        </div>

        <Problem error={fields.error} />

        {fields.loading && !fields.data ? (
          <Loading what="the library" />
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>{search || entity ? "Nothing matches that" : "The library is empty"}</h3>
            <p className="small">
              {search || entity
                ? "Try a different word, or clear the filters."
                : "Define the first field and it becomes available to every product."}
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Belongs to</th>
                  <th>Used by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <Link to={`/fields/${row.key}`}>{row.label}</Link>
                      {!row.active && <span className="chip chip-warn" style={{ marginLeft: 8 }}>Retired</span>}
                      {row.helpText && <div className="small muted truncate" style={{ maxWidth: 380 }}>{row.helpText}</div>}
                    </td>
                    <td><span className="key">{row.key}</span></td>
                    <td>
                      <span className="chip">{DATA_TYPE_LABELS[row.dataType as DataType]}</span>
                      {row.domainLabel && <div className="small muted">{row.domainLabel}</div>}
                    </td>
                    <td>
                      {row.entityLabel}
                      {row.entityRepeating && <span className="chip chip-info" style={{ marginLeft: 6 }}>many</span>}
                    </td>
                    <td>{row.useCount === 0 ? <span className="muted small">—</span> : `${row.useCount} product${row.useCount === 1 ? "" : "s"}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows.length === 0 && !fields.loading && (entities.data?.entities.length ?? 0) === 0 && (
        <Empty title="Start with an entity">
          A field describes something — an applicant, a location, a vehicle. <Link to="/entities">Create an entity</Link>{" "}
          first, then the fields that hang off it.
        </Empty>
      )}
    </div>
  );
}
